import { Component, inject, input, output } from '@angular/core';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FormEvents,
  FormSchemaModel,
  FormScript,
  FormSubmitAction,
  FormWebhook
} from '../../../shared/dynamic-form/form-schema.models';
import { DynamicEntity } from '../dynamic-api.service';
import { FormBuilderEventsPanelComponent } from './form-builder-events-panel.component';
import { FormBuilderScriptsPanelComponent } from './form-builder-scripts-panel.component';
import { FormBuilderSubmitActionsPanelComponent } from './form-builder-submit-actions-panel.component';
import { FormBuilderWebhooksPanelComponent } from './form-builder-webhooks-panel.component';

@Component({
  standalone: true,
  selector: 'ain-form-builder-actions-panel',
  imports: [
    TabsModule,
    InfoHintComponent,
    FormBuilderEventsPanelComponent,
    FormBuilderScriptsPanelComponent,
    FormBuilderWebhooksPanelComponent,
    FormBuilderSubmitActionsPanelComponent
  ],
  template: `
    <section class="actions-panel card">
      <ain-info-hint
        [titleKey]="'dynamic.builder.actions.infoTitle'"
        [descriptionKey]="'dynamic.builder.actions.infoDescription'"
        [bulletKeys]="['dynamic.builder.actions.infoBullet']" />

      <p-tabs value="events">
        <p-tablist>
          <p-tab value="events">{{ t('dynamic.builder.actions.tab.events') }}</p-tab>
          <p-tab value="scripts">{{ t('dynamic.builder.actions.tab.scripts') }}</p-tab>
          <p-tab value="webhooks">{{ t('dynamic.builder.actions.tab.webhooks') }}</p-tab>
          <p-tab value="submit">{{ t('dynamic.builder.actions.tab.submitActions') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="events">
            <ain-form-builder-events-panel
              [schema]="schema()"
              [culture]="culture()"
              [scripts]="schema().scripts ?? []"
              [webhooks]="schema().webhooks ?? []"
              [canEdit]="canEdit()"
              (eventsChange)="eventsChange.emit($event)" />
          </p-tabpanel>
          <p-tabpanel value="scripts">
            <ain-form-builder-scripts-panel
              [scripts]="schema().scripts ?? []"
              [canEdit]="canEdit()"
              (scriptsChange)="scriptsChange.emit($event)" />
          </p-tabpanel>
          <p-tabpanel value="webhooks">
            <ain-form-builder-webhooks-panel
              [webhooks]="schema().webhooks ?? []"
              [canEdit]="canEdit()"
              (webhooksChange)="webhooksChange.emit($event)" />
          </p-tabpanel>
          <p-tabpanel value="submit">
            <ain-form-builder-submit-actions-panel
              [schema]="schema()"
              [culture]="culture()"
              [submitActions]="schema().submitActions ?? []"
              [entities]="entities()"
              [entityFieldMap]="entityFieldMap()"
              [canEdit]="canEdit()"
              (submitActionsChange)="submitActionsChange.emit($event)" />
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </section>
  `,
  styles: [`
    .actions-panel { display: grid; gap: 12px; padding: 16px; }
  `]
})
export class FormBuilderActionsPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly entities = input<DynamicEntity[]>([]);
  readonly entityFieldMap = input<Record<string, { label: string; value: string }[]>>({});
  readonly canEdit = input(true);

  readonly eventsChange = output<FormEvents>();
  readonly scriptsChange = output<FormScript[]>();
  readonly webhooksChange = output<FormWebhook[]>();
  readonly submitActionsChange = output<FormSubmitAction[]>();

  t(key: string): string {
    return this.localization.translate(key);
  }
}
